/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useState} from 'react';
import {useNavigate} from 'react-router';
import {queryClient} from 'views/QueryClientWrapper';

type AdminOverdueOutcome = {
  metric_id: number;
  title: string;
  due_date: string;
  workstream_id: number;
  workstream_name: string;
  recipient_roles: string;
};

type AdminOverdueOutcomeSyncResponse = {
  employee: {
    user_id: string;
    ldap: string;
    email?: string | null;
    name?: string | null;
    time_zone?: string | null;
  };
  local_date: string;
  synced: boolean;
  active_notification_id?: number | null;
  overdue_outcome_count: number;
  notification_created: boolean;
  notification_archived: boolean;
  receipts_inserted: number;
  email_scheduled: boolean;
  email_task_id?: string | null;
  outcomes: AdminOverdueOutcome[];
};

type SyncRequest = {
  ldap: string;
  localDate: string;
  sync: boolean;
  sendEmail: boolean;
};

const todayDateInputValue = () => new Date().toISOString().slice(0, 10);

const formatDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Request failed';

const postOverdueOutcomeActionItemSync = ({ldap, localDate, sync, sendEmail}: SyncRequest) =>
  __request(OpenAPI, {
    method: 'POST',
    url: '/api/v1/admin/notifications/overdue-outcomes/sync',
    body: {
      ldap,
      local_date: localDate || null,
      sync,
      send_email: sendEmail,
    },
    errors: {
      400: 'Invalid request',
      403: 'Not authorized',
      404: 'Employee not found',
      422: 'Validation error',
    },
  }) as Promise<AdminOverdueOutcomeSyncResponse>;

const ResultRow = ({label, value}: {label: string; value: string}) => (
  <Box style={{display: 'flex', justifyContent: 'space-between', gap: 12}}>
    <Text color="subtle">{label}</Text>
    <Text>{value}</Text>
  </Box>
);

const SyncResult = ({result}: {result: AdminOverdueOutcomeSyncResponse}) => (
  <Stack gap="16">
    <Box
      borderRadius="Large"
      borderWidth="1"
      borderStyle="Solid"
      borderColor="Border Subtle"
      padding="16"
      backgroundColor="Background Base"
    >
      <Stack gap="8">
        <Text isBold>
          {result.employee.name || result.employee.ldap} ({result.employee.ldap})
        </Text>
        <ResultRow label="Local date" value={formatDate(result.local_date)} />
        <ResultRow label="Timezone" value={result.employee.time_zone || 'Unavailable'} />
        <ResultRow label="Overdue milestones" value={`${result.overdue_outcome_count}`} />
        <ResultRow
          label="Active notification"
          value={result.active_notification_id ? `#${result.active_notification_id}` : 'None'}
        />
        <ResultRow label="Synced" value={result.synced ? 'Yes' : 'No'} />
        <ResultRow
          label="Created notification"
          value={result.notification_created ? 'Yes' : 'No'}
        />
        <ResultRow
          label="Archived notification"
          value={result.notification_archived ? 'Yes' : 'No'}
        />
        <ResultRow label="Receipts inserted" value={`${result.receipts_inserted}`} />
        <ResultRow label="Email scheduled" value={result.email_scheduled ? 'Yes' : 'No'} />
        {result.email_task_id ? (
          <ResultRow label="Email task" value={result.email_task_id} />
        ) : null}
      </Stack>
    </Box>

    <Box
      borderRadius="Large"
      borderWidth="1"
      borderStyle="Solid"
      borderColor="Border Subtle"
      padding="16"
      backgroundColor="Background Base"
    >
      <Stack gap="12">
        <Text isBold>Current overdue milestones</Text>
        {result.outcomes.length === 0 ? (
          <Text color="subtle">No overdue milestones for this employee/date.</Text>
        ) : (
          <Stack gap="8">
            {result.outcomes.map((outcome) => (
              <Box
                key={`${outcome.metric_id}-${outcome.workstream_id}`}
                borderRadius="Medium"
                borderWidth="1"
                borderStyle="Solid"
                borderColor="Border Subtle"
                padding="12"
              >
                <Stack gap="4">
                  <Text isBold>{outcome.title}</Text>
                  <Text color="subtle">
                    {outcome.workstream_name} · due {formatDate(outcome.due_date)} ·{' '}
                    {outcome.recipient_roles}
                  </Text>
                  <Text size="small" color="subtle">
                    metric #{outcome.metric_id} · workstream #{outcome.workstream_id}
                  </Text>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  </Stack>
);

export const AdminOverdueOutcomeActionItems = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();
  const [ldap, setLdap] = useState('');
  const [localDate, setLocalDate] = useState(todayDateInputValue());
  const [result, setResult] = useState<AdminOverdueOutcomeSyncResponse | null>(null);

  useDocumentTitle('Overdue milestone action items');

  const mutation = useMutation({
    mutationFn: postOverdueOutcomeActionItemSync,
    onSuccess: async (response) => {
      setResult(response);
      await queryClient.invalidateQueries({queryKey: ['notifications']});
    },
  });

  if (!pulseUser) {
    return null;
  }

  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  const normalizedLdap = ldap.trim();
  const hasRequestInFlight = mutation.isPending;

  const run = (sync: boolean, sendEmail = false) => {
    if (!normalizedLdap) {
      return;
    }
    mutation.mutate({ldap: normalizedLdap, localDate, sync, sendEmail});
  };

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Overdue milestone action items', to: ROUTE_PATHS.ADMIN_OVERDUE_OUTCOMES},
      ]}
    >
      <Stack gap="24" style={{maxWidth: 800}}>
        <Stack gap="8">
          <Title size={24}>Overdue milestone action items</Title>
          <Text color="subtle">
            Preview or sync the aggregate overdue-milestone action item for one employee.
          </Text>
        </Stack>

        <Box
          borderRadius="Large"
          borderWidth="1"
          borderStyle="Solid"
          borderColor="Border Subtle"
          padding="16"
          backgroundColor="Background Base"
        >
          <Stack gap="12">
            {mutation.error ? (
              <SystemBanner tone="alert">
                <Text>{getErrorMessage(mutation.error)}</Text>
              </SystemBanner>
            ) : null}

            <Box style={{display: 'grid', gridTemplateColumns: '1fr 180px', gap: 12}}>
              <Box>
                <Text size="small" color="subtle">
                  LDAP
                </Text>
                <TextInput
                  aria-label="Employee LDAP"
                  placeholder="alice"
                  value={ldap}
                  onChange={(event) => setLdap(event.currentTarget.value)}
                  disabled={hasRequestInFlight}
                />
              </Box>
              <Box>
                <Text size="small" color="subtle">
                  Local date
                </Text>
                <TextInput
                  aria-label="Local date"
                  type="date"
                  value={localDate}
                  onChange={(event) => setLocalDate(event.currentTarget.value)}
                  disabled={hasRequestInFlight}
                />
              </Box>
            </Box>

            <Box style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
              <Button
                variant="outline"
                disabled={!normalizedLdap || hasRequestInFlight}
                isLoading={
                  hasRequestInFlight &&
                  mutation.variables?.sync === false &&
                  mutation.variables?.sendEmail === false
                }
                onClick={() => run(false)}
              >
                Preview
              </Button>
              <Button
                variant="primary"
                disabled={!normalizedLdap || hasRequestInFlight}
                isLoading={
                  hasRequestInFlight &&
                  mutation.variables?.sync === true &&
                  mutation.variables?.sendEmail === false
                }
                onClick={() => run(true)}
              >
                Sync action item
              </Button>
              <Button
                variant="outline"
                disabled={!normalizedLdap || hasRequestInFlight}
                isLoading={
                  hasRequestInFlight &&
                  mutation.variables?.sync === true &&
                  mutation.variables?.sendEmail === true
                }
                onClick={() => run(true, true)}
              >
                Sync + send email
              </Button>
            </Box>
          </Stack>
        </Box>

        {result ? <SyncResult result={result} /> : null}
      </Stack>
    </Layout.Container>
  );
};
